<template>
  <div class="wizard-container">
    <slot v-if="showPreWizard" name="pre-wizard"></slot>

    <!-- Main wizard content -->
    <div v-else class="wizard-content">
      <!-- Summary section (optional) -->
      <div v-if="showSummary">
        <!-- Custom summary slot (if provided) -->
        <slot v-if="$slots.summary" name="summary" :data="summaryData"></slot>

        <!-- Built-in summary template -->
        <div v-else-if="summaryItems && summaryItems.length > 0" class="wizard-summary">
          <div class="summary-row">
            <div v-for="(item, index) in summaryItems" :key="index" class="summary-item">
              <i v-if="item.icon" :class="item.icon"></i>
              <span class="summary-label">{{ item.label }}:</span>
              <span class="summary-value">{{ item.value }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Step indicators and content -->
      <WizardSteps :steps="steps" :current-step="wizard.currentStep.value" :show-description="showStepDescription"
        :allow-click="allowStepClick" :orientation="computedOrientation" @step-click="handleStepClick">
        <!-- Pass step content as slots to WizardSteps -->
        <template v-for="(step, index) in steps" :key="index" #[`step-${index}`]>
          <slot :name="`step-${index}`" :step="index" :stepConfig="step" :isFirstStep="index === 0"
            :isLastStep="index === steps.length - 1" :goToNext="wizard.nextStep" :goToPrevious="wizard.previousStep"
            :goToStep="wizard.goToStep" :validateStep="wizard.validateCurrentStep" :data="stepData"></slot>
        </template>
      </WizardSteps>

      <!-- Completion screen (optional) -->
      <slot v-if="showComplete" name="complete" :result="completeResult"></slot>
    </div>

    <!-- Optional button panel -->
    <div v-if="showButtons && !showComplete && !showPreWizard" class="wizard-buttons">
      <button type="button" class="wizard-button wizard-button-back" @click="handleBackButton"
        :disabled="computedDisableBack" data-testid="wizard-back-button">
        <i v-if="computedBackIcon" :class="computedBackIcon"></i>
        <span>{{ computedBackLabel }}</span>
      </button>
      <button type="button" class="wizard-button wizard-button-next" @click="handleNextButton"
        :disabled="computedDisableNext" data-testid="wizard-next-button">
        <span>{{ computedNextLabel }}</span>
        <i v-if="computedNextIcon" :class="computedNextIcon"></i>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardSteps from './wizard/WizardSteps.vue';
import { useWizard, type WizardEmitFn } from '@/composables/useWizard.ts';
import { log } from '@/utils/DebugLogger.ts';

const { t } = useI18n();

interface WizardStep {
  title: string;
  validate?: () => boolean;
  [key: string]: any;
}

interface SummaryItem {
  icon?: string;
  label: string;
  value: any;
}

const props = withDefaults(defineProps<{
  steps: WizardStep[];
  modelValue?: number;
  linear?: boolean;
  allowStepClick?: boolean;
  showButtons?: boolean;
  customButtons?: boolean;
  backLabel?: string | ((step: number) => string) | null;
  nextLabel?: string | ((step: number) => string) | null;
  backIcon?: string | ((step: number) => string) | null;
  nextIcon?: string | ((step: number) => string) | null;
  disableBack?: boolean | ((step: number) => boolean);
  disableNext?: boolean | ((step: number) => boolean);
  orientation?: 'auto' | 'horizontal' | 'vertical';
  showStepDescription?: boolean;
  showSummary?: boolean;
  summaryData?: Record<string, any>;
  summaryItems?: SummaryItem[];
  showPreWizard?: boolean;
  showComplete?: boolean;
  completeResult?: Record<string, any>;
  confirmOnCancel?: boolean;
  loading?: boolean;
  stepData?: Record<string, any>;
}>(), {
  modelValue: undefined,
  linear: true,
  allowStepClick: false,
  showButtons: true,
  customButtons: false,
  backLabel: null,
  nextLabel: null,
  backIcon: null,
  nextIcon: null,
  disableBack: false,
  disableNext: false,
  orientation: 'auto',
  showStepDescription: true,
  showSummary: false,
  summaryData: () => ({}),
  summaryItems: () => [],
  showPreWizard: false,
  showComplete: false,
  completeResult: () => ({}),
  confirmOnCancel: false,
  loading: false,
  stepData: () => ({}),
});

const emit = defineEmits<{
  'update:modelValue': [value: number];
  'step-change': [step: number];
  'before-step-change': [step: number];
  'step-validated': [step: number];
  'step-validation-failed': [step: number];
  'next': [];
  'back': [];
  'finish': [];
  'cancel': [];
  'reset': [];
  'complete': [];
}>();

// Initialize wizard composable
const wizard = useWizard(props, emit as WizardEmitFn);

// Device detection for auto orientation
const isMobile = ref<boolean>(false);

const checkMobile = (): void => {
  isMobile.value = window.innerWidth < 768;
};

onMounted(() => {
  if (props.orientation === 'auto') {
    checkMobile();
    window.addEventListener('resize', checkMobile);
  }
});

onBeforeUnmount(() => {
  if (props.orientation === 'auto') {
    window.removeEventListener('resize', checkMobile);
  }
});

// Computed orientation based on prop or device detection
const computedOrientation = computed((): 'horizontal' | 'vertical' => {
  if (props.orientation === 'auto') {
    return isMobile.value ? 'vertical' : 'horizontal';
  }
  return props.orientation;
});

// Computed button properties
const computedBackLabel = computed((): string => {
  if (props.customButtons && props.backLabel) {
    return typeof props.backLabel === 'function'
      ? props.backLabel(wizard.currentStep.value)
      : props.backLabel;
  }

  // Default behavior
  if (wizard.isFirstStep.value) {
    return t('Cancelar');
  }
  return t('Voltar');
});

const computedBackIcon = computed((): string => {
  if (props.customButtons && props.backIcon) {
    return typeof props.backIcon === 'function'
      ? props.backIcon(wizard.currentStep.value)
      : props.backIcon;
  }

  // Default behavior
  if (wizard.isFirstStep.value) {
    return 'pi pi-times';
  }
  return 'pi pi-arrow-left';
});

const computedNextLabel = computed((): string => {
  if (props.customButtons && props.nextLabel) {
    return typeof props.nextLabel === 'function'
      ? props.nextLabel(wizard.currentStep.value)
      : props.nextLabel;
  }

  // Default behavior
  if (props.loading) {
    return t('Processando...');
  }
  if (wizard.isLastStep.value) {
    return t('Finalizar');
  }
  return t('Próximo');
});

const computedNextIcon = computed((): string => {
  if (props.customButtons && props.nextIcon) {
    return typeof props.nextIcon === 'function'
      ? props.nextIcon(wizard.currentStep.value)
      : props.nextIcon;
  }

  // Default behavior
  if (wizard.isLastStep.value) {
    return 'pi pi-check';
  }
  return 'pi pi-arrow-right';
});

const computedDisableBack = computed((): boolean => {
  if (props.customButtons && props.disableBack) {
    return typeof props.disableBack === 'function'
      ? props.disableBack(wizard.currentStep.value)
      : props.disableBack;
  }

  // Default: never disable back button
  return false;
});

const computedDisableNext = computed((): boolean => {
  if (props.loading) {
    return true;
  }

  if (props.customButtons && props.disableNext) {
    return typeof props.disableNext === 'function'
      ? props.disableNext(wizard.currentStep.value)
      : props.disableNext;
  }

  // Default behavior - disable if validation fails
  return !wizard.validateCurrentStep();
});

// Methods
const handleBackButton = (): void => {
  log('Wizard', 'handleBackButton', 'Back button clicked', { currentStep: wizard.currentStep.value });

  if (wizard.isFirstStep.value) {
    // On first step, cancel
    handleCancel();
  } else {
    // Otherwise, go to previous step
    wizard.previousStep();
    emit('back');
  }
};

const handleNextButton = (): void => {
  log('Wizard', 'handleNextButton', 'Next button clicked', { currentStep: wizard.currentStep.value });

  if (wizard.isLastStep.value) {
    // On last step, finish
    handleFinish();
  } else {
    // Otherwise, go to next step
    const success = wizard.nextStep();
    if (success) {
      emit('next');
    }
  }
};

const handleCancel = (): void => {
  log('Wizard', 'handleCancel', 'Canceling wizard');

  if (props.confirmOnCancel && wizard.currentStep.value > 0) {
    // TODO: Show confirmation dialog
    // For now, just emit cancel
  }

  emit('cancel');
  wizard.reset();
};

const handleFinish = (): void => {
  log('Wizard', 'handleFinish', 'Finishing wizard');

  // Validate last step
  if (!wizard.validateCurrentStep()) {
    log('Wizard', 'handleFinish', 'Last step validation failed');
    return;
  }

  emit('finish');
};

const handleStepClick = (stepIndex: number): void => {
  log('Wizard', 'handleStepClick', 'Step clicked', stepIndex);

  if (props.allowStepClick) {
    wizard.goToStep(stepIndex);
  }
};

// Expose methods for parent access
defineExpose({
  // Wizard state
  currentStep: wizard.currentStep,
  totalSteps: wizard.totalSteps,
  isFirstStep: wizard.isFirstStep,
  isLastStep: wizard.isLastStep,

  // Wizard methods
  goToStep: wizard.goToStep,
  nextStep: wizard.nextStep,
  previousStep: wizard.previousStep,
  validateCurrentStep: wizard.validateCurrentStep,
  reset: wizard.reset,
  complete: wizard.complete
});
</script>

<style scoped>
.wizard-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.wizard-content {
  flex: 1;
  overflow-x: hidden;
  max-width: 100%;
}

/* Built-in Summary Styling */
.wizard-summary {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  border-left: 4px solid var(--app-info-color);
}

.summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: center;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
}

.summary-item i {
  font-size: 0.875rem;
  color: var(--app-info-color);
  flex-shrink: 0;
}

.summary-label {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
}

.summary-value {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
}

/* Button Panel */
.wizard-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 0 0 0;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.wizard-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.wizard-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.wizard-button-back {
  background-color: #f8f9fa;
  color: rgba(0, 0, 0, 0.87);
  border: 1px solid rgba(0, 0, 0, 0.23);
}

.wizard-button-back:not(:disabled):hover {
  background-color: #e9ecef;
}

.wizard-button-next {
  background-color: var(--app-primary-color);
  color: white;
}

.wizard-button-next:not(:disabled):hover {
  background-color: var(--app-primary-color-dark);
}

.wizard-button i {
  font-size: 0.875rem;
}

/* Responsive */
@media (max-width: 768px) {
  .wizard-container {
    gap: 16px;
  }

  .wizard-summary {
    padding: 10px 12px;
  }

  .summary-row {
    gap: 12px;
  }

  .summary-item {
    font-size: 0.8125rem;
  }

  .wizard-buttons {
    padding: 12px 0 0 0;
    gap: 8px;
  }

  .wizard-button {
    padding: 8px 16px;
    font-size: 0.8125rem;
  }

  .wizard-button span {
    display: none;
  }

  .wizard-button i {
    margin: 0;
  }
}
</style>
